import type { ReactNode } from "react";
import { Download } from "lucide-react";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";
import { RunStatusBadge } from "@/components/organisms/studio/RunStatusBadge";
import { ArtifactList } from "@/components/organisms/studio/ArtifactList";
import { InlineArtifactPreview } from "@/components/organisms/studio/InlineArtifactPreview";
import { Button } from "@/components/atoms/studio/ui/button";
import { downloadReportAs } from "@/lib/studio/reportPdf";
import { runErrorLabel } from "@/lib/studio/utils";
import { ImproveInMultiAgentCTA } from "@/components/molecules/ImproveInMultiAgentCTA";
import type { RunRecord, RunArtifact, RunStepRecord, RunStatusValue } from "@/services/studio/types";

// Single source of truth for the migration run report — rendered identically after
// a run completes (TemplateWorkspace), from Run History, and from the Reports page.
// Intentionally just the RESULT + the FILES: the run's markdown answer and the
// downloadable artifacts. Run metadata / inputs / step tables / raw evidence are not
// shown here (they live in the JSON evidence artifact for anyone who wants them).
export function PlaybookRunReportView({
  run,
  artifacts,
  title,
  improving,
  onImprove,
}: {
  run: RunRecord;
  artifacts: RunArtifact[];
  steps: RunStepRecord[];
  title: string;
  improving: boolean;
  onImprove: () => void;
}) {
  // The PDF stays downloadable (Downloads) but is excluded from the inline preview —
  // an embedded PDF iframe is noise in the report body.
  const previewArtifacts = artifacts.filter((a) => !a.name.toLowerCase().endsWith(".pdf"));

  return (
    <div className="space-y-5 p-5">
      {/* ── Weave CTA: sent to the top so deeper analysis is one tap away ───── */}
      {run.status !== "running" && (run.result_md || artifacts.length > 0) && (
        <ImproveInMultiAgentCTA improving={improving} onImprove={onImprove} />
      )}

      {/* ── Result: the answer, first and prominent ────────────────────────── */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/20 px-5 py-3">
          <RunStatusBadge status={run.status as RunStatusValue} />
          {run.result_md && (
            <div className="flex shrink-0 items-center gap-1.5">
              <span className="mr-0.5 hidden font-mono text-[10px] uppercase tracking-wide text-muted-foreground/60 sm:inline">Download</span>
              <Button type="button" variant="outline" size="sm" onClick={() => downloadReportAs(run.result_md || "", "md", title)}>
                <Download className="mr-1.5 h-3.5 w-3.5" />MD
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => downloadReportAs(run.result_md || "", "pdf", title)}>
                <Download className="mr-1.5 h-3.5 w-3.5" />PDF
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => downloadReportAs(run.result_md || "", "docx", title)}>
                <Download className="mr-1.5 h-3.5 w-3.5" />Word
              </Button>
            </div>
          )}
        </div>
        <div className="p-5">
          {run.error_message ? (
            <p className="font-mono text-sm text-destructive">{runErrorLabel(run.error_message)}</p>
          ) : run.result_md ? (
            <SkillResultMarkdown content={run.result_md} />
          ) : (
            <p className="font-mono text-xs text-muted-foreground">No report was saved for this run.</p>
          )}
        </div>
      </div>

      {/* ── Files ───────────────────────────────────────────────────────────── */}
      {previewArtifacts.length > 0 && (
        <SectionCard title="Files" meta={`${previewArtifacts.length} file${previewArtifacts.length === 1 ? "" : "s"}`}>
          <InlineArtifactPreview runId={run.id} artifacts={previewArtifacts} orgId={run.org_id} sourceSurface="playbook" />
        </SectionCard>
      )}
      {artifacts.length > 0 && (
        <SectionCard title="Downloads" meta={`${artifacts.length} file${artifacts.length === 1 ? "" : "s"}`}>
          <ArtifactList runId={run.id} artifacts={artifacts} orgId={run.org_id} />
        </SectionCard>
      )}
    </div>
  );
}

// ── Shared atoms ──────────────────────────────────────────────────────────────
function SectionCard({ title, meta, children }: { title: string; meta?: string; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border bg-muted/20 px-5 py-3">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</span>
        {meta && <span className="font-mono text-[10px] text-muted-foreground/60">{meta}</span>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}
